'use client';

import { forwardRef, ComponentRef, ReactNode, ComponentPropsWithoutRef, Dispatch, SetStateAction, MouseEventHandler } from 'react';
import { Root, Portal, Overlay, Close, Content } from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import close from '@/assets/icons/close.svg';
import styles from './Dialog.module.scss';

const Dialog = Root;

const DialogPortal = Portal;

const DialogClose = Close;

const DialogOverlay = forwardRef<ComponentRef<typeof Overlay>, ComponentPropsWithoutRef<typeof Overlay>>(({ className, ...props }, ref) => (
  <Overlay ref={ref} className={`${styles.overlay} ${className || ''}`} {...props} />
));
DialogOverlay.displayName = Overlay.displayName;

const DialogContent = forwardRef<ComponentRef<typeof Content>, ComponentPropsWithoutRef<typeof Content>>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <Content ref={ref} className={`${styles.content} ${className || ''}`} {...props}>
      {children}
    </Content>
  </DialogPortal>
));
DialogContent.displayName = Content.displayName;

interface CustomDialogProps {
  children?: ReactNode;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onBtnClick?: MouseEventHandler<HTMLButtonElement>;
  btnText?: string;
  btnDisabled?: boolean;
  onSecondaryBtnClick?: MouseEventHandler<HTMLButtonElement>;
  secondaryBtnText?: string;
  secondaryBtnDisabled?: boolean;
  /** Extra class on the dialog surface, so a caller can theme its own popup. */
  className?: string;
  /** Skip the footer button when the dialog's own content already carries its action. */
  hideActionButton?: boolean;
  /** Let the surface shrink to its content instead of holding the reserved minimum height. */
  fitContent?: boolean;
}

const CustomDialog = ({
  children,
  open,
  setOpen,
  onBtnClick = () => {
    setOpen(false);
  },
  btnText,
  btnDisabled = false,
  onSecondaryBtnClick,
  secondaryBtnText,
  secondaryBtnDisabled = false,
  className,
  hideActionButton = false,
  fitContent = false,
}: CustomDialogProps) => {
  const { t } = useTranslation();
  const mainButton = (
    <button className={`${styles.button} body-lr-semibold`} onClick={onBtnClick} disabled={btnDisabled}>
      {btnText ? t(btnText) : t('OK')}
    </button>
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className={`${fitContent ? styles.fitContent : ''} ${className || ''}`}>
        <DialogClose className="p-[12px]">
          <img src={close} alt="" />
        </DialogClose>
        {/* ph-mask: תוכן דיאלוגים נוטה לשאת פרטים אישיים (הערות משרד, פרטי משתמשים) -
            מיסוך גורף בהקלטות PostHog עדיף כאן על קריאות ה-replay (HAT-1081). */}
        <div className={`${styles.childrenContainer} ph-mask`}>
          <div className={styles.childrenWrapper}>{children}</div>
          {hideActionButton ? null : secondaryBtnText ? (
            <div className={styles.actions}>
              {mainButton}
              <button className={`${styles.button} ${styles.secondaryButton} body-lr-semibold`} onClick={onSecondaryBtnClick} disabled={secondaryBtnDisabled}>
                {t(secondaryBtnText)}
              </button>
            </div>
          ) : (
            mainButton
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomDialog;
